import { defaultBusiness as NAP } from "@/lib/site-config";
// Public read + admin write for site_config keys used by the trust section,
// the compliance/legal block, and the per-service routing targets.
// All writes are gated by the same ADMIN_PASSWORD as the analytics dashboard.

import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { assertAdmin } from "@/lib/admin-guard";
import { z } from "zod";
import { type CtaMode, DEFAULT_CTA_MODE, normaliseCtaMode } from "@/lib/cta-mode";

function getAdminClient() {
  const url = process.env.SUPABASE_URL;
  const key = process.env.SUPABASE_SERVICE_ROLE_KEY;
  if (!url || !key) throw new Error("Server configuration error");
  return import("@supabase/supabase-js").then(({ createClient }) =>
    createClient(url, key, { auth: { persistSession: false, autoRefreshToken: false } })
  );
}

/* ──────────────────────────────────────────────────────────────────
   TRUST SECTION — editable copy block
   ────────────────────────────────────────────────────────────────── */

export interface TrustContent {
  eyebrow: string;
  heading: string;
  headingAccent: string;
  intro: string;
  rating: string;
  reviewCount: string;
  reviewSnippets: string[];
  credentials: { title: string; detail: string; sub: string }[];
}

export const defaultTrustContent: TrustContent = {
  eyebrow: "Why drivers trust us",
  heading: "Credentials that",
  headingAccent: "actually mean something.",
  intro:
    "Woodseer Autos is a DVSA-authorised MOT testing station in East London. We look after MOTs, servicing, diagnostics and repairs for local drivers — and we'll always explain what the car needs before we start work.",
  rating: "4.4",
  reviewCount: "116",
  reviewSnippets: [
    "Quick, fair price and clear explanation.",
    "Told us what actually needed doing — and what could wait.",
    "Easy to book and kept us updated throughout.",
  ],
  credentials: [
    {
      title: "DVSA Authorised",
      detail: "MOT Station V106376",
      sub: "Approved testing for classes 1, 2, 3, 4, 5 & 7",
    },
    {
      title: "Workmanship Warranty",
      detail: "On qualifying repairs",
      sub: "Ask us about cover when you book — we'll confirm what's included",
    },
    {
      title: "Quick Turnaround",
      detail: "Where possible",
      sub: "Many jobs done same-day depending on parts and workshop load",
    },
    {
      title: "Cars & Light Vans",
      detail: "All makes welcome",
      sub: "From everyday runabouts to executive and premium marques",
    },
  ],
};

/* ──────────────────────────────────────────────────────────────────
   COMPLIANCE — single source of truth for legal / structured data
   ────────────────────────────────────────────────────────────────── */

export interface ComplianceContent {
  legalName: string;
  tradingName: string;
  vatNumber: string;
  companyNumber: string;
  vtsNumber: string;
  motClasses: string;
  insurance: string;
  /** Verified address used by every JSON-LD payload */
  streetAddress: string;
  addressLocality: string;
  postalCode: string;
  addressCountry: string;
  entranceInstructions: string;
  /** Areas served — used in LocalBusiness areaServed and copy blocks */
  serviceCoverage: string[];
  /** Each service the workshop offers (kept in sync with site-config services) */
  serviceCatalog: string[];
  /** Opening hours as DayOfWeek/opens/closes triples */
  openingHours: { days: string[]; opens: string; closes: string }[];
  disclaimers: string[];
  /** Arrival / parking / reception FAQs surfaced site-wide and on area pages. */
  arrivalFaqs: { q: string; a: string }[];
  /**
   * Admin-controlled MOT availability window. Replaces the previous mock seed
   * so the slot grid reflects what the workshop can actually offer.
   */
  motAvailability: {
    /** How many days into the future to surface (max 60). */
    windowDays: number;
    /** Minimum hours' notice before the next slot can be selected. */
    leadTimeHours: number;
    /** Slot length in minutes (30, 45 or 60). */
    slotMinutes: number;
    /** Maximum slots per day across all opening hours. */
    dailyCapacity: number;
    /** ISO YYYY-MM-DD dates to fully exclude (holidays / closures). */
    blackoutDates: string[];
    /** Specific HH:MM slots already booked, keyed by ISO date. */
    bookedSlots: { date: string; times: string[] }[];
    /** Override the default opening window for specific dates. */
    overrides: { date: string; opens?: string; closes?: string; closed?: boolean }[];
    /**
     * Where MOT callback requests should be routed by default.
     * "phone" → workshop calls the customer back.
     * "whatsapp" → reception messages first via WhatsApp.
     * The lead record stores the preference so admins can triage faster.
     */
    callbackPreference: "phone" | "whatsapp";
  };
  lastReviewed: string;
}

export const defaultCompliance: ComplianceContent = {
  legalName: "Woodseer Autos Ltd",
  tradingName: "Woodseer Autos",
  vatNumber: "",
  companyNumber: "",
  vtsNumber: "V106376",
  motClasses: "1, 2, 3, 4, 5, 7",
  insurance: "Public liability insurance held — certificate available on request",
  streetAddress: "16 Pixley Street",
  addressLocality: "Poplar, London",
  postalCode: "E14 7DF",
  addressCountry: "GB",
  entranceInstructions:
    "Pixley Street is the registered/main address. Vehicles should enter via the gated yard at 14 Copenhagen Place, London E14 7DF — pull through into the workshop car park.",
  serviceCoverage: [
    "Poplar",
    "Canary Wharf",
    "Isle of Dogs",
    "Limehouse",
    "Docklands",
    "Tower Hamlets",
    "Bow",
    "Mile End",
    "Wapping",
  ],
  serviceCatalog: [
    "MOT Testing",
    "Car Servicing",
    "Diagnostics",
    "Mechanical Repairs",
    "Bodywork & Paint",
    "Brake Repair",
    "Clutch & Gearbox",
  ],
  openingHours: [
    { days: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"], opens: "09:00", closes: "18:00" },
  ],
  disclaimers: [
    "All prices quoted are subject to vehicle inspection. We always provide a written estimate before any work begins.",
    "MOT advisories are not failures — they are items to monitor before your next MOT or service.",
    "Reviews displayed on this site reflect verified customer feedback collected via Google and direct request.",
  ],
  motAvailability: {
    windowDays: 14,
    leadTimeHours: 2,
    slotMinutes: 60,
    dailyCapacity: 6,
    blackoutDates: [],
    bookedSlots: [],
    overrides: [],
    callbackPreference: "phone",
  },
  arrivalFaqs: [
    {
      q: "Where exactly is the entrance? It looks like 16 Pixley Street on Google.",
      a: "16 Pixley Street is the registered address — but the actual driveway you pull into is the yard at 14 Copenhagen Place, E14 7DF. Punch '14 Copenhagen Place E14 7DF' into your sat-nav (or the coordinates 51.514512, -0.029928) and you'll arrive at the gated yard entrance.",
    },
    {
      q: "Where do I park when I drop my car off?",
      a: "Pull straight through the Copenhagen Place yard entrance — there are on-site bays for customers right next to the workshop. If they're full when you arrive, the team will wave you to a temporary spot while we book your vehicle in.",
    },
    {
      q: "Can I wait while you work on my car?",
      a: "Yes — we have an indoor waiting area with seating, free Wi-Fi and water. Most MOTs are done within the hour, so plenty of customers prefer to wait. Limehouse DLR and a row of cafés are a 7-minute walk away if you'd rather pop out.",
    },
    {
      q: "How do I find reception once I'm in the yard?",
      a: "After the yard entrance, head straight ahead — reception is the first door on the workshop building. If the shutter is down (rare during opening hours), give the horn a quick beep and someone will come straight out to you.",
    },
    {
      q: "What if I arrive outside opening hours?",
      a: "We're open Monday to Saturday, 9am–6pm. If you need to drop off earlier or collect later, call or WhatsApp us beforehand and we'll do our best to arrange it.",
    },
  ],
  // Static literal — generating with `new Date()` at module load causes
  // SSR/CSR hydration mismatches when the cached payload was rendered on a
  // different day to when the client hydrates. Admins can edit this in /admin.
  lastReviewed: "2026-04-30",
};

/* ──────────────────────────────────────────────────────────────────
   ROUTING — per-service contact targets + per lead-type overrides
   ────────────────────────────────────────────────────────────────── */

export interface LeadTypeRule {
  /** Lead "service" slug or MOT class identifier (e.g. "mot", "mot-class-7", "bodywork") */
  serviceKey: string;
  whatsappNumber?: string;
  whatsappMessage?: string;
  email?: string;
  phone?: string;
  /** Optional human label for admin UI */
  label?: string;
}

export interface RoutingTargets {
  /** Phone number called by the "Call instead" buttons */
  phone: string;
  /** E.164 number used by the WhatsApp links (no + or spaces). */
  whatsappNumber: string;
  /** Default WhatsApp pre-filled message when no service-specific template matches */
  defaultWhatsappMessage: string;
  /** Email recipient for booking notifications */
  bookingEmail: string;
  /** Per-service WhatsApp message template (legacy / site-wide quick chat) */
  serviceMessages: Record<string, string>;
  /** Per lead-type routing overrides (MOT class, service slug) */
  leadTypeRules: LeadTypeRule[];
}

export const defaultRouting: RoutingTargets = {
  phone: NAP.phoneHref.replace("tel:", ""),
  whatsappNumber: "442075372447",
  defaultWhatsappMessage: "Hi, I'd like to make an enquiry",
  bookingEmail: "info@woodseerautos.co.uk",
  serviceMessages: {
    mot: "Hi, I'd like to book an MOT for {reg}",
    "mot-service": "Hi, I'd like to book an MOT and a service together for {reg}",
    servicing: "Hi, I'd like to book a car service for {reg}",
    "full-service": "Hi, I'd like to book a full car service for {reg}",
    "interim-service": "Hi, I'd like to book an interim service for {reg}",
    "major-service": "Hi, I'd like to book a major service for {reg}",
    diagnostics: "Hi, I have a warning light I'd like diagnosed on {reg}",
    "mechanical-repairs": "Hi, I need a repair quote for {reg}",
    brakes: "Hi, I need brake repair for {reg}",
    "clutch-gearbox": "Hi, I need a clutch or gearbox repair for {reg}",
    bodywork: "Hi, I'd like a bodywork repair quote for {reg}",
    repair: "Hi, I need a repair quote for {reg}",
  },
  leadTypeRules: [
    {
      serviceKey: "mot-class-4",
      label: "MOT Class 4 (cars & light vans)",
      whatsappMessage: "Hi, I'd like to book a Class 4 MOT for {reg}",
    },
    {
      serviceKey: "mot-class-7",
      label: "MOT Class 7 (3,000–3,500kg vans)",
      whatsappMessage: "Hi, I'd like to book a Class 7 MOT for {reg}",
    },
    {
      serviceKey: "bodywork",
      label: "Bodywork & Paint enquiries",
      whatsappMessage: "Hi, I'd like a bodywork quote for {reg} — happy to send photos",
    },
  ],
};

/* ──────────────────────────────────────────────────────────────────
   READ — public, used by both server and client
   ────────────────────────────────────────────────────────────────── */

export interface PublicConfigResponse {
  trust: TrustContent;
  routing: RoutingTargets;
  compliance: ComplianceContent;
  ctaMode: CtaMode;
}

function mergeTrust(raw: unknown): TrustContent {
  if (!raw || typeof raw !== "object") return defaultTrustContent;
  const r = raw as Partial<TrustContent>;
  return {
    ...defaultTrustContent,
    ...r,
    reviewSnippets: Array.isArray(r.reviewSnippets) ? r.reviewSnippets : defaultTrustContent.reviewSnippets,
    credentials: Array.isArray(r.credentials) && r.credentials.length > 0 ? r.credentials : defaultTrustContent.credentials,
  };
}
function mergeRouting(raw: unknown): RoutingTargets {
  if (!raw || typeof raw !== "object") return defaultRouting;
  const r = raw as Partial<RoutingTargets>;
  return {
    ...defaultRouting,
    ...r,
    serviceMessages: { ...defaultRouting.serviceMessages, ...(r.serviceMessages || {}) },
    leadTypeRules: Array.isArray(r.leadTypeRules) ? r.leadTypeRules : defaultRouting.leadTypeRules,
  };
}
function mergeCompliance(raw: unknown): ComplianceContent {
  if (!raw || typeof raw !== "object") return defaultCompliance;
  const r = raw as Partial<ComplianceContent>;
  const incomingMot = (r as { motAvailability?: Partial<ComplianceContent["motAvailability"]> }).motAvailability;
  const incomingPref = (incomingMot as { callbackPreference?: string } | undefined)?.callbackPreference;
  const motAvailability: ComplianceContent["motAvailability"] = {
    ...defaultCompliance.motAvailability,
    ...(incomingMot || {}),
    blackoutDates: Array.isArray(incomingMot?.blackoutDates) ? incomingMot!.blackoutDates : [],
    bookedSlots: Array.isArray(incomingMot?.bookedSlots) ? incomingMot!.bookedSlots : [],
    overrides: Array.isArray(incomingMot?.overrides) ? incomingMot!.overrides : [],
    callbackPreference: incomingPref === "whatsapp" ? "whatsapp" : "phone",
  };
  return {
    ...defaultCompliance,
    ...r,
    arrivalFaqs: Array.isArray(r.arrivalFaqs) && r.arrivalFaqs.length > 0
      ? r.arrivalFaqs
      : defaultCompliance.arrivalFaqs,
    serviceCoverage: Array.isArray(r.serviceCoverage) ? r.serviceCoverage : defaultCompliance.serviceCoverage,
    serviceCatalog: Array.isArray(r.serviceCatalog) ? r.serviceCatalog : defaultCompliance.serviceCatalog,
    openingHours: Array.isArray(r.openingHours) ? r.openingHours : defaultCompliance.openingHours,
    disclaimers: Array.isArray(r.disclaimers) ? r.disclaimers : defaultCompliance.disclaimers,
    motAvailability,
  };
}

export const getPublicConfig = createServerFn({ method: "GET" })
  .handler(async (): Promise<PublicConfigResponse> => {
    try {
      const supabase = await getAdminClient();
      const { data } = await supabase
        .from("site_config")
        .select("key, value")
        .in("key", ["trust", "routing", "compliance", "cta_mode"]);
      let trust = defaultTrustContent;
      let routing = defaultRouting;
      let compliance = defaultCompliance;
      let ctaMode: CtaMode = DEFAULT_CTA_MODE;
      for (const row of data || []) {
        const r = row as { key: string; value: unknown };
        if (r.key === "trust") trust = mergeTrust(r.value);
        if (r.key === "routing") routing = mergeRouting(r.value);
        if (r.key === "compliance") compliance = mergeCompliance(r.value);
        if (r.key === "cta_mode") {
          const v = (r.value as { mode?: unknown } | null)?.mode ?? r.value;
          ctaMode = normaliseCtaMode(v);
        }
      }
      return { trust, routing, compliance, ctaMode };
    } catch (err) {
      console.error("getPublicConfig failed", err);
      return { trust: defaultTrustContent, routing: defaultRouting, compliance: defaultCompliance, ctaMode: DEFAULT_CTA_MODE };
    }
  });

const ctaModeWriteSchema = z.object({
  password: z.string().max(200).optional(),
  mode: z.enum(["phone", "form"]),
});

export const saveCtaMode = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .validator((input: unknown) => ctaModeWriteSchema.parse(input))
  .handler(async ({ data, context }) => {
    await assertAdmin(context.userId);
    const supabase = await getAdminClient();
    const { error } = await supabase
      .from("site_config")
      .upsert({ key: "cta_mode", value: { mode: data.mode } as never, updated_at: new Date().toISOString() });
    if (error) throw new Error("Failed to save CTA mode");
    return { success: true };
  });

/* ──────────────────────────────────────────────────────────────────
   WRITE — admin only
   ────────────────────────────────────────────────────────────────── */

const trustWriteSchema = z.object({
  password: z.string().max(200).optional(),
  value: z.object({
    eyebrow: z.string().max(80),
    heading: z.string().max(120),
    headingAccent: z.string().max(120),
    intro: z.string().max(500),
    rating: z.string().max(10),
    reviewCount: z.string().max(10),
    reviewSnippets: z.array(z.string().max(280)).max(8),
    credentials: z
      .array(
        z.object({
          title: z.string().max(80),
          detail: z.string().max(80),
          sub: z.string().max(240),
        })
      )
      .max(8),
  }),
});

export const saveTrustContent = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .validator((input: unknown) => trustWriteSchema.parse(input))
  .handler(async ({ data, context }) => {
    await assertAdmin(context.userId);
    const supabase = await getAdminClient();
    const { error } = await supabase
      .from("site_config")
      .upsert({ key: "trust", value: data.value as never, updated_at: new Date().toISOString() });
    if (error) {
      console.error("saveTrustContent failed", error);
      throw new Error("Failed to save trust content");
    }
    return { success: true };
  });

const leadTypeRuleSchema = z.object({
  serviceKey: z.string().min(1).max(60),
  label: z.string().max(120).optional(),
  whatsappNumber: z.string().max(20).regex(/^\d*$/).optional(),
  whatsappMessage: z.string().max(280).optional(),
  email: z.string().max(255).optional(),
  phone: z.string().max(40).optional(),
});

const routingWriteSchema = z.object({
  password: z.string().max(200).optional(),
  value: z.object({
    phone: z.string().min(5).max(40),
    whatsappNumber: z.string().min(5).max(20).regex(/^\d+$/, "Digits only — no + or spaces"),
    defaultWhatsappMessage: z.string().min(1).max(280),
    bookingEmail: z.string().email().max(255),
    serviceMessages: z.record(z.string().min(1).max(60), z.string().min(1).max(280)),
    leadTypeRules: z.array(leadTypeRuleSchema).max(40),
  }),
});

export const saveRoutingTargets = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .validator((input: unknown) => routingWriteSchema.parse(input))
  .handler(async ({ data, context }) => {
    await assertAdmin(context.userId);
    const supabase = await getAdminClient();
    const { error } = await supabase
      .from("site_config")
      .upsert({ key: "routing", value: data.value as never, updated_at: new Date().toISOString() });
    if (error) {
      console.error("saveRoutingTargets failed", error);
      throw new Error("Failed to save routing targets");
    }
    return { success: true };
  });

const complianceWriteSchema = z.object({
  password: z.string().max(200).optional(),
  value: z.object({
    legalName: z.string().min(1).max(200),
    tradingName: z.string().min(1).max(200),
    vatNumber: z.string().max(40),
    companyNumber: z.string().max(40),
    vtsNumber: z.string().max(40),
    motClasses: z.string().max(60),
    insurance: z.string().max(400),
    streetAddress: z.string().min(1).max(200),
    addressLocality: z.string().min(1).max(80),
    postalCode: z.string().min(1).max(20),
    addressCountry: z.string().min(2).max(2),
    entranceInstructions: z.string().max(500),
    serviceCoverage: z.array(z.string().min(1).max(80)).max(40),
    serviceCatalog: z.array(z.string().min(1).max(80)).max(40),
    openingHours: z
      .array(
        z.object({
          days: z.array(z.string().min(1).max(20)).min(1).max(7),
          opens: z.string().regex(/^\d{2}:\d{2}$/),
          closes: z.string().regex(/^\d{2}:\d{2}$/),
        })
      )
      .max(7),
    disclaimers: z.array(z.string().min(1).max(600)).max(20),
    arrivalFaqs: z
      .array(
        z.object({
          q: z.string().min(1).max(240),
          a: z.string().min(1).max(800),
        })
      )
      .max(20),
    motAvailability: z.object({
      windowDays: z.number().int().min(1).max(60),
      leadTimeHours: z.number().int().min(0).max(168),
      slotMinutes: z.number().int().min(15).max(120),
      dailyCapacity: z.number().int().min(1).max(40),
      blackoutDates: z.array(z.string().regex(/^\d{4}-\d{2}-\d{2}$/)).max(120),
      bookedSlots: z
        .array(
          z.object({
            date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
            times: z.array(z.string().regex(/^\d{2}:\d{2}$/)).max(40),
          })
        )
        .max(120),
      overrides: z
        .array(
          z.object({
            date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
            opens: z.string().regex(/^\d{2}:\d{2}$/).optional(),
            closes: z.string().regex(/^\d{2}:\d{2}$/).optional(),
            closed: z.boolean().optional(),
          })
        )
        .max(60),
      callbackPreference: z.enum(["phone", "whatsapp"]).default("phone"),
    }),
    lastReviewed: z.string().max(40),
  }),
});

export const saveComplianceContent = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .validator((input: unknown) => complianceWriteSchema.parse(input))
  .handler(async ({ data, context }) => {
    await assertAdmin(context.userId);
    const supabase = await getAdminClient();
    const { error } = await supabase
      .from("site_config")
      .upsert({ key: "compliance", value: data.value as never, updated_at: new Date().toISOString() });
    if (error) {
      console.error("saveComplianceContent failed", error);
      throw new Error("Failed to save compliance content");
    }
    return { success: true };
  });

/* ──────────────────────────────────────────────────────────────────
   HELPERS — used by client and JSON-LD generators
   ────────────────────────────────────────────────────────────────── */

/**
 * Resolve the routing target for a given service / lead-type.
 * Looks up the most specific leadTypeRule first, then falls back to
 * defaults. Replaces `{reg}` and `{name}` placeholders if provided.
 */
export function resolveRouting(
  routing: RoutingTargets | null | undefined,
  serviceKey: string | undefined,
  context?: { reg?: string; name?: string }
): { phone: string; whatsappNumber: string; whatsappUrl: string; email: string; message: string } {
  const safeRouting = mergeRouting(routing || defaultRouting);
  const rule = serviceKey
    ? (safeRouting.leadTypeRules || []).find((r) => r.serviceKey === serviceKey)
    : undefined;
  const phone = rule?.phone || safeRouting.phone || NAP.phoneHref.replace("tel:", "");
  const whatsappNumber = (rule?.whatsappNumber || safeRouting.whatsappNumber || "442075372447").replace(/[^\d]/g, "") || "442075372447";
  const email = rule?.email || safeRouting.bookingEmail || "info@woodseerautos.co.uk";
  const fromTemplates = serviceKey ? safeRouting.serviceMessages[serviceKey] : undefined;
  const baseMessage = rule?.whatsappMessage || fromTemplates || safeRouting.defaultWhatsappMessage || "Hi, I'd like to make an enquiry";
  const reg = (context?.reg || "").toUpperCase().trim();
  const name = (context?.name || "").trim();
  const message = baseMessage
    .replace(/\{reg\}/g, reg || "my vehicle")
    .replace(/\{name\}/g, name || "");
  const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(message)}`;
  return { phone, whatsappNumber, whatsappUrl, email, message };
}

/** Build the canonical LocalBusiness/AutoRepair JSON-LD for the homepage / area pages. */
export function buildLocalBusinessSchema(opts: {
  compliance: ComplianceContent;
  trust: TrustContent;
  routing: RoutingTargets;
  url: string;
  image?: string;
  name?: string;
  areaServed?: string[];
}) {
  const compliance = mergeCompliance(opts.compliance);
  const trust = mergeTrust(opts.trust);
  const routing = mergeRouting(opts.routing);
  const { url, image, name, areaServed } = opts;
  return {
    "@context": "https://schema.org",
    "@type": ["LocalBusiness", "AutoRepair"],
    name: name || compliance.tradingName,
    legalName: compliance.legalName,
    telephone: routing.phone,
    email: routing.bookingEmail,
    url,
    ...(image ? { image } : {}),
    address: {
      "@type": "PostalAddress",
      streetAddress: compliance.streetAddress,
      addressLocality: compliance.addressLocality,
      postalCode: compliance.postalCode,
      addressCountry: compliance.addressCountry,
    },
    openingHoursSpecification: compliance.openingHours.map((h) => ({
      "@type": "OpeningHoursSpecification",
      dayOfWeek: h.days,
      opens: h.opens,
      closes: h.closes,
    })),
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: trust.rating,
      reviewCount: trust.reviewCount,
      bestRating: "5",
      worstRating: "1",
    },
    areaServed: areaServed || compliance.serviceCoverage,
    hasMap: `https://maps.google.com/?q=${encodeURIComponent(
      `${compliance.streetAddress} ${compliance.postalCode}`
    )}`,
    priceRange: "££",
    currenciesAccepted: "GBP",
    paymentAccepted: "Cash, Card",
    ...(compliance.vatNumber ? { vatID: compliance.vatNumber } : {}),
  };
}
